import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authApi = {
  updateProfile: (name) => api.put('/auth/me', { name }),
};

// Events API
export const eventsApi = {
  getAll: () => api.get('/events'),
  getOne: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  duplicate: (id) => api.post(`/events/${id}/duplicate`),
  exportExcel: (id) => api.get(`/events/${id}/export/excel`, { responseType: 'blob' }),
};

// Phases API
export const phasesApi = {
  getAll: (eventId) => api.get(`/events/${eventId}/phases`),
  create: (eventId, data) => api.post(`/events/${eventId}/phases`, data),
  update: (eventId, phaseId, data) => api.put(`/events/${eventId}/phases/${phaseId}`, data),
  delete: (eventId, phaseId) => api.delete(`/events/${eventId}/phases/${phaseId}`),
  duplicate: (eventId, phaseId) => api.post(`/events/${eventId}/phases/${phaseId}/duplicate`),
  reorder: (eventId, phaseIds) => api.post(`/events/${eventId}/phases/reorder`, { phase_ids: phaseIds }),
};

// Speakers API
export const speakersApi = {
  getAll: (eventId) => api.get(`/events/${eventId}/speakers`),
  create: (eventId, data) => api.post(`/events/${eventId}/speakers`, data),
  update: (eventId, speakerId, data) => api.put(`/events/${eventId}/speakers/${speakerId}`, data),
  delete: (eventId, speakerId) => api.delete(`/events/${eventId}/speakers/${speakerId}`),
};

// Microphones API
export const microphonesApi = {
  getAll: (eventId) => api.get(`/events/${eventId}/microphones`),
  create: (eventId, data) => api.post(`/events/${eventId}/microphones`, data),
  update: (eventId, micId, data) => api.put(`/events/${eventId}/microphones/${micId}`, data),
  assign: (eventId, micId, speakerId) => api.post(
    speakerId
      ? `/events/${eventId}/microphones/${micId}/assign?speaker_id=${speakerId}`
      : `/events/${eventId}/microphones/${micId}/assign`
  ),
  delete: (eventId, micId) => api.delete(`/events/${eventId}/microphones/${micId}`),
};

// Cameras API
export const camerasApi = {
  getAll: (eventId) => api.get(`/events/${eventId}/cameras`),
  create: (eventId, data) => api.post(`/events/${eventId}/cameras`, data),
  update: (eventId, cameraId, data) => api.put(`/events/${eventId}/cameras/${cameraId}`, data),
  delete: (eventId, cameraId) => api.delete(`/events/${eventId}/cameras/${cameraId}`),
};

// Comments API
export const commentsApi = {
  getAll: (eventId) => api.get(`/events/${eventId}/comments`),
  createForEvent: (eventId, content) => api.post(`/events/${eventId}/comments`, { content }),
  createForPhase: (eventId, phaseId, content) => api.post(`/events/${eventId}/phases/${phaseId}/comments`, { content }),
  delete: (eventId, commentId) => api.delete(`/events/${eventId}/comments/${commentId}`),
  updateStatus: (eventId, commentId, status) => api.patch(`/events/${eventId}/comments/${commentId}/status`, { status }),
};

// Clients API (organizer only)
export const clientsApi = {
  getAll: (eventId) => api.get(`/events/${eventId}/clients`),
  invite: (eventId, email) => api.post(`/events/${eventId}/clients`, { email }),
  revoke: (eventId, userId) => api.delete(`/events/${eventId}/clients/${userId}`),
  revokePending: (eventId, inviteId) => api.delete(`/events/${eventId}/clients/pending/${inviteId}`),
};

// Screens API (pantallas dinámicas del evento)
export const screensApi = {
  getAll: (eventId) => api.get(`/events/${eventId}/screens`),
  create: (eventId, data) => api.post(`/events/${eventId}/screens`, data),
  update: (eventId, screenId, data) => api.put(`/events/${eventId}/screens/${screenId}`, data),
  delete: (eventId, screenId) => api.delete(`/events/${eventId}/screens/${screenId}`),
};

// Screen values API — { screen_id: { content_type, content_url } }
export const screenValuesApi = {
  update: (eventId, phaseId, values) =>
    api.put(`/events/${eventId}/phases/${phaseId}/screen-values`, { values }),
};

// Upload API
export const uploadApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Timer API
export const timerApi = {
  action: (eventId, action, initialElapsed = undefined) => api.post(`/events/${eventId}/timer`, {
    action,
    ...(initialElapsed !== undefined && { initial_elapsed: initialElapsed }),
  }),
  updateElapsed: (eventId, elapsed, phaseElapsed) =>
    api.post(`/events/${eventId}/timer/update-elapsed?elapsed=${elapsed}&phase_elapsed=${phaseElapsed}`),
};

export default api;
